import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import Modal from '../common/modal.tsx';
import Button from '../common/button.tsx';
import { cn } from '../../utils/common-utils.ts';
import { capitalizeWords } from '../../utils/capitalize-words.ts';
import {
  GET_QUESTION_LOG_FACETS,
  GetQuestionLogFacetsQuery,
  GetQuestionLogFacetsQueryVariables,
  QuestionLogFilterInput,
  QuestionLogTaxonomyFilter,
} from '../../apollo/question-log.ts';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  filter: QuestionLogTaxonomyFilter;
  scopeFilter: QuestionLogFilterInput;
  onApply: (filter: QuestionLogTaxonomyFilter) => void;
}

const toggleId = (ids: number[] | undefined, id: number) => {
  const next = ids?.includes(id)
    ? ids.filter((currentId) => currentId !== id)
    : [...(ids ?? []), id];

  return next.length ? next : undefined;
};

const buildFacetFilter = (
  scopeFilter: QuestionLogFilterInput,
  overrides: QuestionLogTaxonomyFilter,
): QuestionLogFilterInput => ({
  ...scopeFilter,
  ...overrides,
});

const FilterColumn = ({
  title,
  items,
  selectedIds,
  onToggle,
}: {
  title: string;
  items: { id: number; name: string; count: number }[];
  selectedIds?: number[];
  onToggle: (id: number) => void;
}) => (
  <div className="flex min-h-80 flex-col rounded-2xl border border-table-border bg-white">
    <div className="border-b border-table-border px-4 py-3 text-sm font-semibold">
      {title}
    </div>
    <div className="flex flex-col gap-2 p-3">
      {items.length ? (
        items.map((item) => {
          const selected = selectedIds?.includes(item.id);

          return (
            <button
              key={item.id}
              onClick={() => onToggle(item.id)}
              className={cn(
                'flex items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition-colors',
                selected
                  ? 'border-blue bg-light-blue text-dark-blue'
                  : 'border-light-gray hover:border-table-border hover:bg-light-gray',
              )}
            >
              <span className="pr-4">{capitalizeWords(item.name)}</span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-semibold',
                  selected
                    ? 'bg-white text-dark-blue'
                    : 'bg-light-blue text-blue',
                )}
              >
                {item.count}
              </span>
            </button>
          );
        })
      ) : (
        <div className="rounded-xl bg-light-gray px-3 py-4 text-sm text-gray">
          No matches
        </div>
      )}
    </div>
  </div>
);

export const QuestionLogFilterModal = ({
  isOpen,
  onClose,
  filter,
  scopeFilter,
  onApply,
}: IProps) => {
  const [draftFilter, setDraftFilter] = useState<QuestionLogTaxonomyFilter>(filter);

  useEffect(() => {
    if (isOpen) {
      setDraftFilter(filter);
    }
  }, [filter, isOpen]);

  const { data: baseData } = useQuery<
    GetQuestionLogFacetsQuery,
    GetQuestionLogFacetsQueryVariables
  >(GET_QUESTION_LOG_FACETS, {
    variables: {
      filter: scopeFilter,
    },
    skip: !isOpen,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const sectionFacetFilter = useMemo(
    () => buildFacetFilter(scopeFilter, {}),
    [scopeFilter],
  );

  const domainFacetFilter = useMemo(
    () =>
      buildFacetFilter(scopeFilter, {
        sectionIds: draftFilter.sectionIds,
      }),
    [draftFilter.sectionIds, scopeFilter],
  );

  const skillFacetFilter = useMemo(
    () =>
      buildFacetFilter(scopeFilter, {
        sectionIds: draftFilter.sectionIds,
        domainIds: draftFilter.domainIds,
      }),
    [draftFilter.domainIds, draftFilter.sectionIds, scopeFilter],
  );

  const qtypeFacetFilter = useMemo(
    () =>
      buildFacetFilter(scopeFilter, {
        sectionIds: draftFilter.sectionIds,
        domainIds: draftFilter.domainIds,
        skillIds: draftFilter.skillIds,
      }),
    [
      draftFilter.domainIds,
      draftFilter.sectionIds,
      draftFilter.skillIds,
      scopeFilter,
    ],
  );

  const { data: sectionData, loading: sectionsLoading } = useQuery<
    GetQuestionLogFacetsQuery,
    GetQuestionLogFacetsQueryVariables
  >(GET_QUESTION_LOG_FACETS, {
    variables: {
      filter: sectionFacetFilter,
    },
    skip: !isOpen,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { data: domainData, loading: domainsLoading } = useQuery<
    GetQuestionLogFacetsQuery,
    GetQuestionLogFacetsQueryVariables
  >(GET_QUESTION_LOG_FACETS, {
    variables: {
      filter: domainFacetFilter,
    },
    skip: !isOpen,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { data: skillData, loading: skillsLoading } = useQuery<
    GetQuestionLogFacetsQuery,
    GetQuestionLogFacetsQueryVariables
  >(GET_QUESTION_LOG_FACETS, {
    variables: {
      filter: skillFacetFilter,
    },
    skip: !isOpen,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { data: qtypeData, loading: qtypesLoading } = useQuery<
    GetQuestionLogFacetsQuery,
    GetQuestionLogFacetsQueryVariables
  >(GET_QUESTION_LOG_FACETS, {
    variables: {
      filter: qtypeFacetFilter,
    },
    skip: !isOpen,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const baseFacets = baseData?.questionLog?.getQuestionLogFacets;
  const loading =
    sectionsLoading || domainsLoading || skillsLoading || qtypesLoading;

  const counts = useMemo(() => {
    const sectionCounts = new Map(
      (sectionData?.questionLog?.getQuestionLogFacets?.sections ?? []).map(
        (item) => [item.id, item.count],
      ),
    );
    const domainCounts = new Map(
      (domainData?.questionLog?.getQuestionLogFacets?.domains ?? []).map(
        (item) => [item.id, item.count],
      ),
    );
    const skillCounts = new Map(
      (skillData?.questionLog?.getQuestionLogFacets?.skills ?? []).map(
        (item) => [item.id, item.count],
      ),
    );
    const qtypeCounts = new Map(
      (qtypeData?.questionLog?.getQuestionLogFacets?.qtypes ?? []).map(
        (item) => [item.id, item.count],
      ),
    );

    return { sectionCounts, domainCounts, skillCounts, qtypeCounts };
  }, [domainData, qtypeData, sectionData, skillData]);

  const relationships = useMemo(() => {
    const domainById = new Map(
      (baseFacets?.domains ?? []).map((item) => [item.id, item]),
    );
    const skillById = new Map(
      (baseFacets?.skills ?? []).map((item) => [item.id, item]),
    );
    const qtypeById = new Map(
      (baseFacets?.qtypes ?? []).map((item) => [item.id, item]),
    );

    return { domainById, skillById, qtypeById };
  }, [baseFacets]);

  const contextIds = useMemo(() => {
    const sectionIds = new Set<number>(draftFilter.sectionIds ?? []);
    const domainIds = new Set<number>(draftFilter.domainIds ?? []);
    const skillIds = new Set<number>(draftFilter.skillIds ?? []);
    const qtypeIds = new Set<number>(draftFilter.qtypeIds ?? []);

    draftFilter.domainIds?.forEach((domainId) => {
      const domain = relationships.domainById.get(domainId);
      if (domain?.sectionId) {
        sectionIds.add(domain.sectionId);
      }
    });

    draftFilter.skillIds?.forEach((skillId) => {
      const skill = relationships.skillById.get(skillId);
      if (!skill) {
        return;
      }

      skillIds.add(skill.id);
      domainIds.add(skill.domainId);
      const domain = relationships.domainById.get(skill.domainId);
      if (domain?.sectionId) {
        sectionIds.add(domain.sectionId);
      }
    });

    draftFilter.qtypeIds?.forEach((qtypeId) => {
      const qtype = relationships.qtypeById.get(qtypeId);
      if (!qtype) {
        return;
      }

      qtypeIds.add(qtype.id);
      sectionIds.add(qtype.sectionId);

      if (qtype.domainId) {
        domainIds.add(qtype.domainId);
      }

      if (qtype.skillId) {
        skillIds.add(qtype.skillId);
        const skill = relationships.skillById.get(qtype.skillId);
        if (skill) {
          domainIds.add(skill.domainId);
          const domain = relationships.domainById.get(skill.domainId);
          if (domain?.sectionId) {
            sectionIds.add(domain.sectionId);
          }
        }
      } else if (qtype.domainId) {
        const domain = relationships.domainById.get(qtype.domainId);
        if (domain?.sectionId) {
          sectionIds.add(domain.sectionId);
        }
      }
    });

    return { sectionIds, domainIds, skillIds, qtypeIds };
  }, [draftFilter, relationships]);

  const withCounts = <T extends { id: number; count: number }>(
    items: T[],
    countMap: Map<number, number>,
  ) => items.map((item) => ({ ...item, count: countMap.get(item.id) ?? 0 }));

  const visibleSections = useMemo(() => {
    if (contextIds.sectionIds.size) {
      return withCounts(
        (baseFacets?.sections ?? []).filter((section) =>
          contextIds.sectionIds.has(section.id),
        ),
        counts.sectionCounts,
      );
    }

    return withCounts(baseFacets?.sections ?? [], counts.sectionCounts);
  }, [baseFacets?.sections, contextIds.sectionIds, counts.sectionCounts]);

  const visibleDomains = useMemo(() => {
    if (contextIds.domainIds.size) {
      return withCounts(
        (baseFacets?.domains ?? []).filter((domain) =>
          contextIds.domainIds.has(domain.id),
        ),
        counts.domainCounts,
      );
    }

    if (!draftFilter.sectionIds?.length) {
      return withCounts(baseFacets?.domains ?? [], counts.domainCounts);
    }

    return withCounts(
      (baseFacets?.domains ?? []).filter((domain) =>
        draftFilter.sectionIds?.includes(domain.sectionId),
      ),
      counts.domainCounts,
    );
  }, [
    baseFacets?.domains,
    contextIds.domainIds,
    counts.domainCounts,
    draftFilter.sectionIds,
  ]);

  const visibleSkills = useMemo(() => {
    const skills = baseFacets?.skills ?? [];

    if (contextIds.skillIds.size) {
      return withCounts(
        skills.filter((skill) => contextIds.skillIds.has(skill.id)),
        counts.skillCounts,
      );
    }

    if (draftFilter.domainIds?.length) {
      return withCounts(
        skills.filter((skill) => draftFilter.domainIds?.includes(skill.domainId)),
        counts.skillCounts,
      );
    }

    if (draftFilter.sectionIds?.length) {
      return withCounts(
        skills.filter((skill) => {
          const domain = relationships.domainById.get(skill.domainId);

          return draftFilter.sectionIds?.includes(domain?.sectionId ?? -1);
        }),
        counts.skillCounts,
      );
    }

    return withCounts(skills, counts.skillCounts);
  }, [
    baseFacets?.skills,
    contextIds.skillIds,
    counts.skillCounts,
    draftFilter.domainIds,
    draftFilter.sectionIds,
    relationships.domainById,
  ]);

  const visibleQtypes = useMemo(() => {
    const qtypes = baseFacets?.qtypes ?? [];
    const filteredQtypes =
      qtypeData?.questionLog?.getQuestionLogFacets?.qtypes ?? [];

    if (contextIds.qtypeIds.size) {
      return withCounts(
        qtypes.filter((qtype) => contextIds.qtypeIds.has(qtype.id)),
        counts.qtypeCounts,
      );
    }

    if (
      draftFilter.skillIds?.length ||
      draftFilter.domainIds?.length ||
      draftFilter.sectionIds?.length
    ) {
      return filteredQtypes;
    }

    return withCounts(qtypes, counts.qtypeCounts);
  }, [
    baseFacets?.qtypes,
    contextIds.qtypeIds,
    counts.qtypeCounts,
    draftFilter.domainIds,
    draftFilter.sectionIds,
    draftFilter.skillIds,
    qtypeData,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Advanced Filters"
      description="Select SAT sections, domains, skills, and qtypes, then apply them to your Question Log."
      fullScreen
    >
      <div className="flex h-full flex-col">
        <div className="mt-4 grid gap-4 lg:grid-cols-4">
          <FilterColumn
            title="Section"
            items={visibleSections}
            selectedIds={draftFilter.sectionIds}
            onToggle={(id) => {
              setDraftFilter((current) => ({
                ...current,
                sectionIds: toggleId(current.sectionIds, id),
                domainIds: undefined,
                skillIds: undefined,
                qtypeIds: undefined,
              }));
            }}
          />

          <FilterColumn
            title="Domain"
            items={visibleDomains}
            selectedIds={draftFilter.domainIds}
            onToggle={(id) => {
              setDraftFilter((current) => ({
                ...current,
                domainIds: toggleId(current.domainIds, id),
                skillIds: undefined,
                qtypeIds: undefined,
              }));
            }}
          />

          <FilterColumn
            title="Skill"
            items={visibleSkills}
            selectedIds={draftFilter.skillIds}
            onToggle={(id) => {
              setDraftFilter((current) => ({
                ...current,
                skillIds: toggleId(current.skillIds, id),
                qtypeIds: undefined,
              }));
            }}
          />

          <FilterColumn
            title="Qtype"
            items={visibleQtypes}
            selectedIds={draftFilter.qtypeIds}
            onToggle={(id) => {
              setDraftFilter((current) => ({
                ...current,
                qtypeIds: toggleId(current.qtypeIds, id),
              }));
            }}
          />
        </div>

        <div className="sticky bottom-0 mt-6 flex flex-col gap-3 rounded-2xl border border-table-border bg-white px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-sm text-gray">
            {loading
              ? 'Refreshing counts...'
              : 'Counts reflect the current scope at each level.'}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button white onClick={() => setDraftFilter({})}>
              Clear
            </Button>
            <Button
              onClick={() => {
                onApply(draftFilter);
                onClose();
              }}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
